import apiClient from './client'
import type { PaginatedResponse } from '@/types'

// Types
export interface DocumentCategory {
  id: number
  name: string
  slug: string
  description: string
  icon: string
  documents_count: number
}

export interface Document {
  id: number
  slug: string
  title: string
  description: string
  category: DocumentCategory | null
  file_url: string
  file_name: string
  file_type: string
  file_size: number
  thumbnail_url: string | null
  uploaded_by: {
    id: number
    full_name: string
    avatar: string | null
  }
  downloads_count: number
  is_public: boolean
  tags: string[]
  created_at: string
  updated_at: string
}

export interface Folder {
  id: number
  name: string
  parent_id: number | null
  documents_count: number
  subfolders_count: number
  created_at: string
}

export const documentsApi = {
  // Categories
  getCategories: async (): Promise<DocumentCategory[]> => {
    const response = await apiClient.get('/documents/categories/')
    // Handle both array and paginated response formats
    if (Array.isArray(response.data)) {
      return response.data
    }
    return response.data.results || []
  },

  // Documents
  getDocuments: async (params?: {
    category?: string
    folder?: number
    search?: string
    file_type?: string
    ordering?: string
    page?: number
  }): Promise<PaginatedResponse<Document>> => {
    const response = await apiClient.get('/documents/', { params })
    return response.data
  },

  getDocument: async (id: number): Promise<Document> => {
    const response = await apiClient.get(`/documents/${id}/`)
    return response.data
  },

  uploadDocument: async (data: FormData): Promise<Document> => {
    const response = await apiClient.post('/documents/', data, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  },

  updateDocument: async (id: number, data: {
    title?: string
    description?: string
    category_id?: number
    tags?: string[]
    is_public?: boolean
  }): Promise<Document> => {
    const response = await apiClient.patch(`/documents/${id}/`, data)
    return response.data
  },

  deleteDocument: async (slug: string): Promise<void> => {
    await apiClient.delete(`/documents/${slug}/`)
  },

  downloadDocument: async (slug: string): Promise<Blob> => {
    const response = await apiClient.get(`/documents/${slug}/download/`, {
      responseType: 'blob',
    })
    return response.data
  },

  // Folders - NOTE: Backend uses categories, not folders
  // These methods are kept for compatibility but may not work
  getFolders: async (_parentId?: number): Promise<Folder[]> => {
    // Backend doesn't have folders endpoint, return empty array
    return []
  },

  createFolder: async (_data: {
    name: string
    parent_id?: number
  }): Promise<Folder> => {
    throw new Error('Folders not supported - use categories instead')
  },

  updateFolder: async (_id: number, _name: string): Promise<Folder> => {
    throw new Error('Folders not supported - use categories instead')
  },

  deleteFolder: async (_id: number): Promise<void> => {
    throw new Error('Folders not supported - use categories instead')
  },

  // Search
  searchDocuments: async (query: string): Promise<Document[]> => {
    const response = await apiClient.get('/documents/search/', {
      params: { q: query },
    })
    return response.data
  },

  // Recent
  getRecentDocuments: async (limit?: number): Promise<Document[]> => {
    const response = await apiClient.get('/documents/recent/', {
      params: { limit },
    })
    return response.data
  },
}
